import React, { useEffect, useRef, useState } from "react";
import { getDocument } from "pdfjs-dist";
import "pdfjs-dist/build/pdf.worker.entry";

// onErreur : appelé si le PDF ne peut pas être lu (réseau, fichier, délai dépassé) pour proposer une autre lecture.
export default function PdfCanvasPreview({ url, title = "Aperçu PDF", className = "", onErreur = null, delaiMax = 45000 }) {
  const containerRef = useRef(null);
  const [state, setState] = useState({ loading: Boolean(url), error: "" });

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !url) {
      setState({ loading: false, error: "" });
      return undefined;
    }

    let cancelled = false;
    let loadingTask;
    const renderTasks = [];
    container.replaceChildren();
    setState({ loading: true, error: "" });

    const controleur = new AbortController();
    // Un téléchargement qui ne répond plus ne doit pas laisser « Chargement… » indéfiniment.
    const minuterie = setTimeout(() => controleur.abort(), delaiMax);
    const renderPdf = async () => {
      try {
        const response = await fetch(url, { signal: controleur.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.arrayBuffer();
        clearTimeout(minuterie);
        // isEvalSupported: false — un PDF piégé ne peut pas exécuter de code
        // (correctif officiel de la faille CVE-2024-4367 pour pdf.js 3.x).
        loadingTask = getDocument({ data, isEvalSupported: false });
        const pdf = await loadingTask.promise;

        for (let pageNumber = 1; pageNumber <= pdf.numPages && !cancelled; pageNumber += 1) {
          const page = await pdf.getPage(pageNumber);
          const naturalViewport = page.getViewport({ scale: 1 });
          const availableWidth = Math.max(280, container.clientWidth - 16);
          const cssScale = Math.min(1.7, availableWidth / naturalViewport.width);
          const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
          const renderViewport = page.getViewport({ scale: cssScale * pixelRatio });
          const canvas = document.createElement("canvas");
          canvas.className = "ql-pdf-canvas-page";
          canvas.width = Math.ceil(renderViewport.width);
          canvas.height = Math.ceil(renderViewport.height);
          canvas.style.aspectRatio = `${renderViewport.width} / ${renderViewport.height}`;
          canvas.setAttribute("aria-label", `Page ${pageNumber} sur ${pdf.numPages}`);
          container.appendChild(canvas);
          const task = page.render({ canvasContext: canvas.getContext("2d", { alpha: false }), viewport: renderViewport });
          renderTasks.push(task);
          await task.promise;
        }

        if (!cancelled) setState({ loading: false, error: "" });
      } catch (error) {
        if (!cancelled) {
          setState({ loading: false, error: "Impossible d’afficher le PDF. Réessayez l’aperçu." });
          onErreur?.(error);
        }
      }
    };

    renderPdf();
    return () => {
      cancelled = true;
      clearTimeout(minuterie);
      controleur.abort();
      renderTasks.forEach((task) => task.cancel());
      loadingTask?.destroy();
      container.replaceChildren();
    };
  }, [url]);

  return (
    <div className={`ql-pdf-canvas-preview ${className}`.trim()} aria-label={title}>
      {state.loading && <div className="ql-pdf-canvas-state" role="status"><span className="spinner-border spinner-border-sm" /> Chargement du PDF…</div>}
      {state.error && <div className="ql-pdf-canvas-state error" role="alert">{state.error}</div>}
      <div ref={containerRef} className="ql-pdf-canvas-pages" />
    </div>
  );
}
